import { t as uiText } from '../../i18n';
/**
 * マイク（getUserMedia）周りのエラー処理を共通化するユーティリティ。
 *
 * - `preflightMicCheck()`: getUserMedia 呼び出し前に判別できる失敗ケース
 *   （HTTPS でない / ブラウザ非対応 / マイク未接続）を検出する。
 * - `toMicUserError()`: getUserMedia / MediaRecorder 由来の例外を、ユーザーが
 *   次に取るべきアクションを含む人間向けメッセージに変換する。
 * - `logMicError()`: 音声認識関連のエラーを `/log-error` 経由で errors コレクションに記録する。
 *
 * いずれも `WhisperProvider` / `AdminWhisperProvider` から共通利用される。
 */

export type MicUserError = {
  code: string;
  message: string;
};

/**
 * 音声認識関連のエラーを `/log-error` に送信して Firestore errors コレクションに記録する。
 * fire-and-forget で呼び元をブロックしない。
 */
export function logMicError(
  code: string,
  message: string,
  context: {
    publishId?: string;
    workspaceId?: string;
    extra?: Record<string, unknown>;
  } = {}
): void {
  let inIframe = false;
  try { inIframe = window.self !== window.top; } catch { inIframe = true; }

  const severity: 'info' | 'error' = code === 'NO_SPEECH' ? 'info' : 'error';
  const payload = {
    source: 'speech',
    code,
    message,
    severity,
    context: {
      publishId: context.publishId,
      workspaceId: context.workspaceId,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      extra: { inIframe, ...context.extra },
    },
    occurredAt: Date.now(),
  };

  fetch('/log-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

export type PreflightResult =
  | { ok: true }
  | ({ ok: false } & MicUserError);

/**
 * getUserMedia を呼ぶ前に静的に判別できる失敗ケースを検出する。
 * 失敗時は `{ ok: false, code, message }` を返し、UI 側でそのまま表示できる。
 *
 * NOTE: permissions.query による denied チェックは行わない。
 * Permissions-Policy ヘッダーやクロスオリジン iframe 環境で false positive を
 * 返すケースがあり、getUserMedia() の実呼び出し結果に委ねる方が確実なため。
 */
export async function preflightMicCheck(): Promise<PreflightResult> {
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return {
      ok: false,
      code: 'INSECURE_CONTEXT',
      message:
        uiText("Microphone requires a secure (HTTPS) connection")
    };
  }

  if (!navigator?.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      code: 'UNSUPPORTED_BROWSER',
      message:
        uiText("This browser does not support microphone input. Please try the latest Chrome, Edge, or Safari")
    };
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasMic = devices.some((d) => d.kind === 'audioinput');
    if (!hasMic) {
      return {
        ok: false,
        code: 'NO_MICROPHONE',
        message:
          uiText("No microphone detected. Please connect a microphone and try again")
      };
    }
  } catch {
    // enumerateDevices に失敗しても致命的ではないので getUserMedia に進む
  }

  return { ok: true };
}

/**
 * `getUserMedia()` / `MediaRecorder` 由来のエラーをユーザー向けの
 * 「原因 + 具体的な対処方法」メッセージにマッピングする。
 */
export function toMicUserError(e: unknown): MicUserError {
  const name = (e as { name?: string })?.name ?? '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return {
        code: 'NotAllowedError',
        message:
          uiText("Microphone access was denied. Click the lock icon in the address bar and allow microphone access (on macOS, also enable it under System Settings > Privacy & Security > Microphone)")
      };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return {
        code: 'NotFoundError',
        message:
          uiText("No microphone was found. Please connect a microphone and try again")
      };
    case 'NotReadableError':
    case 'TrackStartError':
      return {
        code: 'NotReadableError',
        message:
          uiText("Microphone is already being used by another app (e.g., Zoom, Meet). Close the other app and try again")
      };
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return {
        code: 'OverconstrainedError',
        message:
          uiText("No available microphone meets the required settings")
      };
    case 'SecurityError':
      return {
        code: 'SecurityError',
        message:
          uiText("Microphone use is blocked in this context. Please access the page over HTTPS")
      };
    case 'AbortError':
      return {
        code: 'AbortError',
        message:
          uiText("Microphone start was aborted. Please try again")
      };
    case 'TypeError':
      return {
        code: 'TypeError',
        message: uiText("Invalid microphone request")
      };
    default:
      return {
        code: name || 'MIC_ERROR',
        message:
          uiText("Failed to start recording. Please reload the page and try again")
      };
  }
}
